import { useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/* ------------------------------------------------------------
   Color palettes — 8 curated options
   Each defines background of the "on-brand" surface + wordmark colors + mark accent.
   ------------------------------------------------------------ */
const PALETTES = [
  { id: "navy-gold",       label: "Signature",       sub: "Navy & Gold",         bg: "#050B14", fg: "#F8F9FA", muted: "#8B9BB4", accent: "#D4AF37", tag: "#D4AF37" },
  { id: "onyx-emerald",    label: "Vault",           sub: "Onyx & Emerald",      bg: "#0B0F14", fg: "#F5F7FA", muted: "#8A9AA5", accent: "#0FB574", tag: "#0FB574" },
  { id: "midnight-plat",   label: "Sterling",        sub: "Midnight & Platinum", bg: "#0F1420", fg: "#FFFFFF", muted: "#A9B3C2", accent: "#C7CED9", tag: "#C7CED9" },
  { id: "sapphire-gold",   label: "Sovereign",       sub: "Sapphire & Gold",     bg: "#0A1F3D", fg: "#F5F8FF", muted: "#8FA1BF", accent: "#E5B94D", tag: "#E5B94D" },
  { id: "bordeaux-cream",  label: "Reserve",         sub: "Bordeaux & Cream",    bg: "#3B0D14", fg: "#F5EBDD", muted: "#C6A99F", accent: "#D4AF37", tag: "#D4AF37" },
  { id: "charcoal-copper", label: "Bullion",         sub: "Charcoal & Copper",   bg: "#181818", fg: "#F5F5F5", muted: "#9E9E9E", accent: "#B87333", tag: "#B87333" },
  { id: "black-mono",      label: "Obsidian",        sub: "All-Black Mono",      bg: "#000000", fg: "#FFFFFF", muted: "#8A8A8A", accent: "#FFFFFF", tag: "#FFFFFF" },
  { id: "ivory-forest",    label: "Trust",           sub: "Ivory & Forest",      bg: "#F5F1E8", fg: "#0A1F14", muted: "#6B7A6E", accent: "#065F46", tag: "#065F46" },
];

/* ------------------------------------------------------------
   Refined SVG builder — "gem cut" mark + wordmark + optional tagline
   ------------------------------------------------------------ */
function buildLogoSVG({ palette, tagline, size = "full", surfaceBg }) {
  const { bg, fg, muted, accent } = palette;
  const surface = surfaceBg || bg;

  // wordmark colors: Direct + Cap use fg, Fund uses accent
  const markSize = 40;
  const gap = 14;

  if (size === "mark") {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${markSize} ${markSize}" width="160" height="160">
  <rect width="${markSize}" height="${markSize}" fill="${surface}"/>
  <g>
    <rect x="1.5" y="1.5" width="${markSize - 3}" height="${markSize - 3}" fill="none" stroke="${accent}" stroke-width="1"/>
    <rect x="${markSize/2 - 7}" y="${markSize/2 - 7}" width="14" height="14" fill="${accent}" transform="rotate(45 ${markSize/2} ${markSize/2})"/>
    <circle cx="${markSize/2}" cy="${markSize/2}" r="1.75" fill="${surface}"/>
  </g>
</svg>`;
  }

  const wordmarkX = markSize + gap;
  const tagOffsetY = tagline ? 14 : 0;
  const totalH = 40 + tagOffsetY;
  const viewW = 360;
  const cy = 20;

  const taglineSVG = tagline
    ? `<text x="${wordmarkX + 1}" y="${cy + 24}" font-family="Outfit, 'Helvetica Neue', Arial, sans-serif" font-size="8" letter-spacing="3" font-weight="600" fill="${muted}">
        <tspan>${escapeXml(tagline).toUpperCase()}</tspan>
       </text>`
    : "";

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${viewW} ${totalH}" width="${viewW * 2}" height="${totalH * 2}">
  <rect width="${viewW}" height="${totalH}" fill="${surface}"/>
  <!-- Mark -->
  <g transform="translate(0, ${(totalH - markSize) / 2})">
    <rect x="1.5" y="1.5" width="${markSize - 3}" height="${markSize - 3}" fill="none" stroke="${accent}" stroke-width="1"/>
    <rect x="${markSize/2 - 7}" y="${markSize/2 - 7}" width="14" height="14" fill="${accent}" transform="rotate(45 ${markSize/2} ${markSize/2})"/>
    <circle cx="${markSize/2}" cy="${markSize/2}" r="1.75" fill="${surface}"/>
  </g>
  <!-- Wordmark -->
  <text x="${wordmarkX}" y="${cy + tagOffsetY / 2 + 8}" font-family="Outfit, 'Helvetica Neue', Arial, sans-serif" font-size="26" font-weight="500" letter-spacing="-0.4">
    <tspan fill="${fg}">Direct</tspan><tspan fill="${accent}">Fund</tspan><tspan fill="${fg}">Cap</tspan>
  </text>
  ${taglineSVG}
</svg>`;
}

function escapeXml(s) {
  return String(s || "").replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" }[c]));
}

/* ------------------------------------------------------------
   Client-side PNG rasterizer (via canvas)
   ------------------------------------------------------------ */
async function svgToPng(svgString, scale = 4) {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob), "image/png");
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function download(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ============================================================
   Logo Studio Page
   ============================================================ */
export default function LogoStudio() {
  const [paletteId, setPaletteId] = useState("navy-gold");
  const [tagline, setTagline] = useState("Capital Partners");
  const [showTagline, setShowTagline] = useState(true);
  const [copied, setCopied] = useState(false);

  const palette = useMemo(() => PALETTES.find((p) => p.id === paletteId), [paletteId]);

  const svgFull = useMemo(
    () => buildLogoSVG({ palette, tagline: showTagline ? tagline : "", size: "full" }),
    [palette, tagline, showTagline]
  );
  const svgMark = useMemo(() => buildLogoSVG({ palette, size: "mark" }), [palette]);

  // Versatility previews — same logo rendered on 3 different surfaces
  const svgOnBrand = svgFull;
  const svgOnWhite = useMemo(
    () => buildLogoSVG({
      palette: { ...palette, bg: "#FFFFFF", fg: palette.id === "ivory-forest" ? "#0A1F14" : "#0A1326", muted: "#6B7280" },
      tagline: showTagline ? tagline : "",
      size: "full",
      surfaceBg: "#FFFFFF",
    }),
    [palette, tagline, showTagline]
  );
  const svgOnBlack = useMemo(
    () => buildLogoSVG({
      palette: { ...palette, bg: "#000000", fg: "#FFFFFF", muted: "#8A8A8A" },
      tagline: showTagline ? tagline : "",
      size: "full",
      surfaceBg: "#000000",
    }),
    [palette, tagline, showTagline]
  );

  const doDownload = async (kind) => {
    const filename = `directfundcap-${palette.id}${showTagline ? "-tagline" : ""}`;
    try {
      if (kind === "svg-full") {
        download(`${filename}.svg`, new Blob([svgFull], { type: "image/svg+xml" }));
      } else if (kind === "svg-mark") {
        download(`${filename}-mark.svg`, new Blob([svgMark], { type: "image/svg+xml" }));
      } else if (kind === "png-full") {
        const b = await svgToPng(svgFull, 4);
        download(`${filename}.png`, b);
      } else if (kind === "png-mark") {
        const b = await svgToPng(svgMark, 6);
        download(`${filename}-mark.png`, b);
      }
      toast.success("Downloaded.");
    } catch {
      toast.error("Download failed. Try again.");
    }
  };

  const copySvg = async () => {
    try {
      await navigator.clipboard.writeText(svgFull);
      setCopied(true);
      toast.success("SVG code copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed.");
    }
  };

  return (
    <div data-testid="logo-studio-page" className="min-h-screen bg-[#050B14] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-[#050B14]/90 backdrop-blur z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 border border-gold flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-gold rotate-45" />
            </div>
            <span className="font-heading text-base">Direct<span className="text-gold">Fund</span>Cap</span>
          </Link>
          <span className="text-xs tracking-[0.2em] uppercase text-white/40">Logo Studio</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Controls */}
        <div className="lg:col-span-4">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-gold mb-4">Brand Palette</div>
          <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tight mb-8">
            Design a signature.<br/>Own the wall.
          </h1>

          <div className="grid grid-cols-1 gap-2 mb-8" data-testid="palette-grid">
            {PALETTES.map((p) => (
              <button
                key={p.id}
                onClick={() => setPaletteId(p.id)}
                data-testid={`palette-${p.id}`}
                className={`flex items-center gap-4 p-3 border transition-all text-left ${
                  paletteId === p.id ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/30"
                }`}
              >
                <div className="flex -space-x-1">
                  <span className="w-6 h-6 border border-white/10" style={{ background: p.bg }} />
                  <span className="w-6 h-6 border border-white/10" style={{ background: p.accent }} />
                  <span className="w-6 h-6 border border-white/10" style={{ background: p.fg }} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white">{p.label}</div>
                  <div className="text-[11px] text-white/50">{p.sub}</div>
                </div>
                {paletteId === p.id && <Check className="w-4 h-4 text-gold" />}
              </button>
            ))}
          </div>

          <div className="mb-6 border border-white/10 p-4">
            <label className="flex items-center justify-between mb-3 cursor-pointer">
              <span className="text-xs tracking-[0.18em] uppercase text-white/60">Show tagline</span>
              <input
                type="checkbox"
                checked={showTagline}
                onChange={(e) => setShowTagline(e.target.checked)}
                data-testid="toggle-tagline"
                className="accent-[#D4AF37]"
              />
            </label>
            {showTagline && (
              <input
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                data-testid="tagline-input"
                placeholder="Capital Partners"
                className="w-full bg-[#050B14] border border-white/10 focus:border-gold focus:outline-none text-white px-3 py-2 text-sm"
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => doDownload("svg-full")} data-testid="download-svg-full" className="border border-white/15 hover:border-gold text-white px-3 py-3 text-xs inline-flex items-center justify-center gap-2">
              <Download className="w-3.5 h-3.5" /> SVG · Full
            </button>
            <button onClick={() => doDownload("png-full")} data-testid="download-png-full" className="border border-white/15 hover:border-gold text-white px-3 py-3 text-xs inline-flex items-center justify-center gap-2">
              <Download className="w-3.5 h-3.5" /> PNG · Full
            </button>
            <button onClick={() => doDownload("svg-mark")} data-testid="download-svg-mark" className="border border-white/15 hover:border-gold text-white px-3 py-3 text-xs inline-flex items-center justify-center gap-2">
              <Download className="w-3.5 h-3.5" /> SVG · Mark
            </button>
            <button onClick={() => doDownload("png-mark")} data-testid="download-png-mark" className="border border-white/15 hover:border-gold text-white px-3 py-3 text-xs inline-flex items-center justify-center gap-2">
              <Download className="w-3.5 h-3.5" /> PNG · Mark
            </button>
          </div>

          <button
            onClick={copySvg}
            data-testid="copy-svg"
            className="mt-3 w-full bg-gold hover:bg-gold-400 text-navy-950 font-semibold px-6 py-3 text-sm inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy SVG code"}
          </button>
        </div>

        {/* Previews */}
        <div className="lg:col-span-8 space-y-6">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-gold">Hero Preview · {palette.label}</div>

          {/* On-brand */}
          <div className="border border-white/10 p-8 md:p-14 flex items-center justify-center" style={{ background: palette.bg }} data-testid="preview-on-brand">
            <div dangerouslySetInnerHTML={{ __html: svgFull }} className="max-w-full [&_svg]:max-w-full [&_svg]:h-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <PreviewTile label="On Brand" bg={palette.bg}>
              <div dangerouslySetInnerHTML={{ __html: svgOnBrand }} className="[&_svg]:w-full [&_svg]:h-auto" />
            </PreviewTile>
            <PreviewTile label="On White" bg="#FFFFFF">
              <div dangerouslySetInnerHTML={{ __html: svgOnWhite }} className="[&_svg]:w-full [&_svg]:h-auto" />
            </PreviewTile>
            <PreviewTile label="On Black" bg="#000000">
              <div dangerouslySetInnerHTML={{ __html: svgOnBlack }} className="[&_svg]:w-full [&_svg]:h-auto" />
            </PreviewTile>
          </div>

          {/* Mark showcase */}
          <div className="grid grid-cols-3 gap-4">
            <PreviewTile label="Mark · Brand" bg={palette.bg}>
              <div dangerouslySetInnerHTML={{ __html: svgMark }} className="[&_svg]:w-24 [&_svg]:h-24 mx-auto" />
            </PreviewTile>
            <PreviewTile label="Mark · White" bg="#FFFFFF">
              <div dangerouslySetInnerHTML={{
                __html: buildLogoSVG({ palette: { ...palette, bg: "#FFFFFF" }, size: "mark", surfaceBg: "#FFFFFF" }),
              }} className="[&_svg]:w-24 [&_svg]:h-24 mx-auto" />
            </PreviewTile>
            <PreviewTile label="Mark · Black" bg="#000000">
              <div dangerouslySetInnerHTML={{
                __html: buildLogoSVG({ palette: { ...palette, bg: "#000000", accent: "#FFFFFF" }, size: "mark", surfaceBg: "#000000" }),
              }} className="[&_svg]:w-24 [&_svg]:h-24 mx-auto" />
            </PreviewTile>
          </div>

          {/* Specs */}
          <div className="border border-white/10 p-6 text-sm text-white/60 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Spec label="Background" value={palette.bg} swatch={palette.bg} />
            <Spec label="Wordmark" value={palette.fg} swatch={palette.fg} />
            <Spec label="Accent" value={palette.accent} swatch={palette.accent} />
            <Spec label="Muted" value={palette.muted} swatch={palette.muted} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewTile({ label, bg, children }) {
  return (
    <div>
      <div className="border border-white/10 aspect-[16/7] p-5 flex items-center justify-center overflow-hidden" style={{ background: bg }}>
        {children}
      </div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-2">{label}</div>
    </div>
  );
}

function Spec({ label, value, swatch }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1.5">{label}</div>
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 border border-white/10" style={{ background: swatch }} />
        <span className="font-mono text-xs text-white/80">{value}</span>
      </div>
    </div>
  );
}
